import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { pagePaths, preloadPage } from '../pageLoaders';
import { prefetchDestination } from '../services/pagePrefetch';
import { invalidatePrefetchedData } from '../services/api';
import { canAccessPage, useCompanyCapabilities } from './CompanyCapabilities';

export function canPrefetch() {
  const connection = navigator.connection;
  return document.visibilityState !== 'hidden' && navigator.onLine !== false
    && !connection?.saveData && !['slow-2g', '2g'].includes(connection?.effectiveType);
}

export default function WorkspacePreloader() {
  const { pathname } = useLocation();
  const prioritize = useRef(() => {});
  const currentPath = useRef(pathname);
  currentPath.current = pathname;
  const caps = useCompanyCapabilities();
  const signature = JSON.stringify(caps ? { permissions: caps.permissions, features: caps.features } : null);
  useEffect(() => {
    invalidatePrefetchedData();
    return invalidatePrefetchedData;
  }, [signature]);
  useEffect(() => {
    let stopped = false, idleId, idleTimer, intentTimer, running = false, queued;
    const identity = JSON.stringify([sessionStorage.getItem('access_token'), sessionStorage.getItem('company_slug')]);
    const valid = () => !stopped && canPrefetch() && identity === JSON.stringify([sessionStorage.getItem('access_token'), sessionStorage.getItem('company_slug')]);
    const permissions = JSON.parse(signature);
    const allowed = path => pagePaths.includes(path) && canAccessPage(permissions, path);
    const remaining = pagePaths.filter(allowed).sort((a, b) => Number(b === currentPath.current) - Number(a === currentPath.current));
    const warmed = new Map();
    const completed = new Map();
    const schedule = () => {
      if (stopped || !remaining.length) return;
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(() => {
        const next = async () => {
          if (stopped) return;
          if (running || !valid() || navigator.scheduling?.isInputPending?.()) { schedule(); return; }
          const path = remaining.shift();
          running = true;
          try {
            await preloadPage(path);
            await prefetchDestination(path, permissions, valid, {
              allDomains: true,
              // Yield between requests so interaction and foreground reads take priority.
              beforeRequest: () => new Promise(resolve => window.setTimeout(resolve, 150)),
            });
            warmed.set(path, Date.now());
            completed.set(path, Date.now());
          } catch (_) {
            if (!stopped && !valid()) remaining.unshift(path);
          } finally { running = false; }
          if (queued) drain();
          schedule();
        };
        if (window.requestIdleCallback) idleId = window.requestIdleCallback(next);
        else next();
      }, 1500);
    };
    prioritize.current = path => {
      if (!allowed(path) || Date.now() - (completed.get(path) || 0) < 60000) return;
      const index = remaining.indexOf(path);
      if (index >= 0) remaining.splice(index, 1);
      remaining.unshift(path);
      window.clearTimeout(idleTimer);
      if (idleId !== undefined && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      schedule();
    };
    schedule();
    const drain = async () => {
      if (running || !queued || !valid()) return;
      const path = queued; queued = null; running = true;
      try { await prefetchDestination(path, permissions, valid); warmed.set(path, Date.now()); }
      catch (_) { /* Speculative failures must never interrupt the current page. */ }
      finally { running = false; if (queued) drain(); }
    };
    const intent = event => {
      const element = event.target.closest?.('[data-prefetch], a[href]');
      const target = element?.getAttribute('data-prefetch') || element?.getAttribute('href');
      if (!target || !valid()) return;
      let url;
      try { url = new URL(target, window.location.origin); } catch (_) { return; }
      if (url.origin !== window.location.origin || !allowed(url.pathname) || url.pathname === currentPath.current) return;
      preloadPage(url.pathname).catch(() => {});
      window.clearTimeout(intentTimer);
      intentTimer = window.setTimeout(() => {
        if (Date.now() - (warmed.get(url.pathname) || 0) < 20000) return;
        queued = url.pathname; drain();
      }, 120);
    };
    const invalidate = () => { invalidatePrefetchedData(); warmed.clear(); completed.clear(); prioritize.current(currentPath.current); };
    document.addEventListener('pointerover', intent);
    document.addEventListener('focusin', intent);
    window.addEventListener('quantro:domains-changed', invalidate);
    window.addEventListener('quantro:data-sources-changed', invalidate);
    return () => {
      stopped = true; queued = null; prioritize.current = () => {};
      window.clearTimeout(idleTimer); window.clearTimeout(intentTimer);
      if (idleId !== undefined && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      document.removeEventListener('pointerover', intent);
      document.removeEventListener('focusin', intent);
      window.removeEventListener('quantro:domains-changed', invalidate);
      window.removeEventListener('quantro:data-sources-changed', invalidate);
    };
  }, [signature]);
  useEffect(() => { prioritize.current(pathname); }, [pathname]);
  return null;
}
